import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { Separator, Row, FlexView } from '@cuemath/comps';
import { Button } from '@cuemath/web-comps';
import Search from 'modules/list/new-search-bar';
import { Table, TableCell, Heading } from 'comps';
import { Dropdown, TableRow } from 'semantic-ui-react';
import { useMappingLogsGetAll, invalidateMappingLogs } from 'actions/auto-mapping';
import { openModal } from 'actions/modal';
import { getAll as getPrograms } from 'actions/programs';
import Pagination from 'modules/list/pagination';
import { convertEpochToDateTime } from 'utils';
import ExternalLinkImg from 'img/left-nav/external-link.svg';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import MODAL from 'constants/modal';
import { MODAL_FORM, FORM_ACTIONS } from 'constants/form';
import { CopyButton } from 'comps';
import { autoMappingStates } from '../../constants/constants';
import LAYOUT from 'constants/layout';
import { MAPPING_STATES_OPTIONS } from '../../constants/auto-mapping';

const TableWrapper = styled(Row)`
  margin-top: 24px;
  width: 100%;
`;
const StyledButton = styled(Button)`
  img {
    margin-right: 4px;
  }
`;

const FadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const StyledTableCell = styled(TableCell)`
  cursor: pointer;
`;

const PaginationWrapper = styled(Row)`
  height: 42px;
`;

const Root = styled.div`
  padding: 24px 16px;
  animation: ${FadeIn} 0.5s ease-in-out;
`;

const CustomButton = styled(Button)`
  margin-left: ${LAYOUT.GUTTER}px;
`;

const AutoMapping = ({ actions, programs }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tableFilter, setTableFilter] = useState({
    mappingStates: [],
  });

  const {
    data: mappingLogs,
    getAll: getMappingLogs,
    isStale,
    isProcessing,
  } = useMappingLogsGetAll();

  const fetchMappingLogs = useCallback(() => {
    const query = {
      page,
      search,
      per_page: 20,
      states: [...tableFilter?.mappingStates],
    };
    if (!search) {
      delete query.search;
    }
    if (!query.states.length) {
      delete query.states;
    }
    getMappingLogs(query);
  }, [getMappingLogs, page, tableFilter?.mappingStates, search]);

  useEffect(() => {
    fetchMappingLogs?.();
  }, [fetchMappingLogs]);

  useEffect(() => {
    if (!programs?.length) actions.getPrograms({ is_live: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions]);

  useEffect(() => {
    if (isStale && !isProcessing) {
      fetchMappingLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStale, isProcessing]);

  const handleFilterChange = (key, value) => {
    setTableFilter({
      ...tableFilter,
      [key]: value,
    });
  };

  const invalidateLogs = useCallback(() => {
    invalidateMappingLogs();
  }, []);

  const onPageChange = newPage => {
    setPage(newPage);
  };

  const onSearch = search => {
    setSearch(search);
  };

  const openMappingRatingModal = mappingLogId => {
    actions.openModal(
      MODAL_FORM.MAPPING_RATING,
      {
        size: 'custom',
        width: '400px',
        height: '150px',
      },
      {
        invalidateLogs,
        data: {
          mappingLogId,
        },
      },
    );
  };

  const openAssignDemoModal = (studentId, mappingLogId) => {
    actions.openModal(
      MODAL_FORM.ASSIGN_TEACHER_CONTAINER,
      {
        size: 'custom',
        width: `${Math.ceil((window.innerWidth * 60) / 100)}px`,
        height: MODAL.UPDATE_HEIGHT,
      },
      {
        formType: FORM_ACTIONS.CREATE,
        data: {
          studentId,
          mappingLogId,
        },
        programs,
      },
    );
  };

  const openAutomatedMappingModal = () => {
    actions.openModal(MODAL_FORM.AUTO_MAPPING_MODAL, {
      size: 'custom',
      width: `720px`,
    });
  };

  const renderActionCell = (
    isMappingRated,
    isMappingGood,
    mappingState,
    studentId,
    mappingLogId,
  ) => {
    if (mappingState === autoMappingStates.AUTO_MAPPED) {
      if (!isMappingRated) {
        return (
          <TableCell width={2}>
            <StyledButton
              label="Rate Mapping"
              size="xsmall"
              width="140px"
              backgroundColor="transparent"
              iconConfig={{ imgLink: ExternalLinkImg }}
              onClick={() => openMappingRatingModal(mappingLogId)}
            ></StyledButton>
          </TableCell>
        );
      } else {
        return (
          <TableCell>{`Feedback Submitted: ${isMappingGood ? 'Good' : 'Bad'}`}</TableCell>
        );
      }
    } else if (mappingState === autoMappingStates.MANUAL_MAPPED) {
      return <TableCell>{'Manually Mapped'}</TableCell>;
    } else {
      return (
        <TableCell>
          <StyledButton
            label="Assign Demo"
            size="xsmall"
            width="140px"
            backgroundColor="transparent"
            iconConfig={{ imgLink: ExternalLinkImg }}
            onClick={() => openAssignDemoModal(studentId, mappingLogId)}
          ></StyledButton>
        </TableCell>
      );
    }
  };

  return (
    <Root id="manual-portal-root">
      <Row justifyContent="space-between" alignItems="center">
        <Heading>Automated Mapping</Heading>
        <CustomButton
          size="small"
          label="Find a teacher"
          onClick={openAutomatedMappingModal}
        />
      </Row>
      <Separator height={LAYOUT.GUTTER} transparent />
      <Row justifyContent="flex-end">
        <Search
          showNewSearchBar={true}
          onSearch={onSearch}
          placeholder="Search using Prospect ID"
        />
      </Row>
      <TableWrapper>
        <Table
          headerCell={[
            { title: 'Prospect ID' },
            { title: 'Log Created On' },
            { title: 'Student Name' },
            {
              title: 'Mapping State',
              filterApplied: tableFilter?.mappingStates?.length > 0,
              filterComponent: (
                <Dropdown
                  clearable
                  fluid
                  multiple
                  onChange={(_, data) => {
                    handleFilterChange('mappingStates', data?.value);
                  }}
                  options={MAPPING_STATES_OPTIONS}
                  placeholder="Select State"
                  search
                  selection
                  value={tableFilter?.mappingStates}
                />
              ),
              showFilters: true,
            },
            { title: 'Mapped Teacher' },
            { title: 'Mapped Teacher Score' },
            { title: 'Demo Start Time' },
            { title: 'Auto Mapping Failure Reason' },
            { title: 'Action' },
          ]}
          dataLength={mappingLogs?.length || 0}
          havePagination
          showInfo={false}
          showLoader={isProcessing}
          flexBasis="100%"
        >
          {mappingLogs?.map(
            ({
              id: mappingLogId,
              prospect_id: prospectId,
              student_name: studentName,
              student_id: studentId,
              lsq_link: lsqLink,
              state: mappingState,
              teacher_id: teacherId,
              cumulative_score: cumulativeScore,
              reason,
              demo_start_ts: demoStartTs,
              created_on: createdOn,
              mapped_at_ts: mappedAt,
              is_mapping_rated: isMappingRated,
              is_mapping_good: isMappingGood,
              teacher_name: teacherName,
            }) => (
              <TableRow key={mappingLogId}>
                <StyledTableCell width={4} textTransform="none">
                  <FlexView>
                    <CopyButton
                      copyMessage="Copy Prospect ID"
                      text={prospectId || '-'}
                      onClick={() => {
                        if (!prospectId) return () => {};
                        return window.open(lsqLink, '_blank');
                      }}
                      style={{ position: 'static' }}
                    />
                  </FlexView>
                </StyledTableCell>
                <TableCell width={2}>
                  {createdOn ? convertEpochToDateTime(createdOn * 1000) : '-'}
                </TableCell>
                <StyledTableCell
                  width={2}
                  onClick={() => {
                    if (!studentId) return () => {};
                    return window.open(`/students/${studentId}`, '_blank');
                  }}
                >
                  {studentName || '-'}
                </StyledTableCell>
                <TableCell width={1}>{mappingState || '-'}</TableCell>
                <StyledTableCell
                  width={2}
                  onClick={() => {
                    if (!teacherId) return () => {};
                    return window.open(`/users/teachers/${teacherId}`, '_blank');
                  }}
                >
                  {teacherName || '-'}
                </StyledTableCell>
                <TableCell width={1}>{cumulativeScore || '-'}</TableCell>
                <TableCell width={2}>
                  {demoStartTs ? convertEpochToDateTime(demoStartTs * 1000) : '-'}
                </TableCell>
                <TableCell width={2}>{reason || '--'}</TableCell>
                {renderActionCell(
                  isMappingRated,
                  isMappingGood,
                  mappingState,
                  studentId,
                  mappingLogId,
                )}
              </TableRow>
            ),
          )}
        </Table>
      </TableWrapper>
      <Separator transparent height={8} />
      <PaginationWrapper justifyContent="center" alignItems="center">
        {mappingLogs && (
          <Pagination
            page={page}
            perPage={13}
            count={null}
            onPageChange={onPageChange}
            shouldShowNewUi
          />
        )}
      </PaginationWrapper>
    </Root>
  );
};

AutoMapping.propTypes = {
  programs: PropTypes.array.isRequired,
  actions: PropTypes.shape({
    openModal: PropTypes.func.isRequired,
    getPrograms: PropTypes.func.isRequired,
  }),
};
export default connect(
  ({ programs }) => {
    const programQueryParam = {
      is_live: true,
    };

    return {
      programs: store.getAll(programs, programQueryParam)?.data,
    };
  },
  dispatch => ({
    actions: bindActionCreators(
      {
        openModal,
        getPrograms,
      },
      dispatch,
    ),
  }),
)(AutoMapping);
